/**
 * ss-accordion.js — <ss-accordion>, a multi-open animated accordion.
 *
 * Mirrors the collection filter drawer's accordion (assets/cl-filter-drawer.js): native <details>
 * for semantics + no-JS fallback, JS orchestrates the open/close TIMING, CSS does the motion via the
 * grid-template-rows 0fr↔1fr trick. Each row is independent (multi-open). Rows are theme blocks
 * (blocks/_info-accordion-row.liquid); this element is rendered by blocks/info-accordion.liquid.
 *
 * State (the .is-open class + [open]) survives being MOVED by <ss-modal>'s adopt mode — connected/
 * disconnectedCallback re-bind cleanly on each move.
 *
 * Theme editor: selecting a row (or the wrapper block) opens the modal + expands that row, so the
 * merchant sees what they're editing.
 */
(function () {
  if (customElements.get("ss-accordion")) return;

  // Auto-open hint timing. The modal's entrance is 0.35s (snippets/ss-modal.liquid), so wait for it
  // to settle before growing the panel. STAGGER is the beat between rows when more than one opens —
  // shorter than a row's own 550ms expansion on purpose, so they overlap and read as one cascade
  // rather than as separate events.
  var AUTO_OPEN_DELAY = 300;
  var AUTO_OPEN_STAGGER = 50;

  function expand(item) {
    // Render content first ([open]), then flip to is-open on the next frame so the transition runs.
    item.open = true;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        item.classList.add("is-open");
      });
    });
  }

  function collapse(item) {
    var body = item.querySelector(".ss-acc__body");
    item.classList.remove("is-open"); // triggers the collapse transition
    if (!body) {
      item.open = false;
      return;
    }
    // Drop [open] (native hide) only once the height transition finishes.
    var onEnd = function (ev) {
      if (ev.target !== body || ev.propertyName !== "grid-template-rows")
        return;
      body.removeEventListener("transitionend", onEnd);
      if (!item.classList.contains("is-open")) item.open = false;
    };
    body.addEventListener("transitionend", onEnd);
  }

  class SSAccordion extends HTMLElement {
    connectedCallback() {
      this.classList.add("js-accordion");
      // Sync any rows that shipped [open] to is-open (no animation on first paint / after a move).
      var open = this.querySelectorAll(".ss-acc__item[open]");
      for (var i = 0; i < open.length; i++) open[i].classList.add("is-open");

      this._onClick = this.onClick.bind(this);
      this.addEventListener("click", this._onClick);
      this.autoOpenInModal();
    }

    disconnectedCallback() {
      if (this._onClick) this.removeEventListener("click", this._onClick);
      this.clearAutoOpen();
    }

    // Affordance hint: when this accordion is ADOPTED into <ss-modal> (ss-modal MOVES it, so
    // connectedCallback fires on every open), expand the first N rows so it's obvious the rows are
    // clickable. Opt-in per consumer via [data-auto-open] — inert for the product-details accordion,
    // which has neither the attribute nor a modal ancestor. A bare attribute means one row;
    // data-auto-open="2" opens two, staggered.
    autoOpenInModal() {
      this.clearAutoOpen();

      var raw = this.getAttribute("data-auto-open");
      if (raw === null) return;
      var count = parseInt(raw, 10);
      if (!count || count < 1) count = 1; // bare attribute, or junk, = one row

      // No <ss-modal> ancestor = this is the at-rest connect in the hidden adopt-host (or the
      // move back home on close), not a modal open.
      var modal = this.closest("ss-modal");
      if (!modal) return;

      // Theme editor: shopify:block:select already opens the modal + expands the SELECTED row.
      // Don't also fire the hint on top of it every time the merchant clicks a block.
      if (window.Shopify && window.Shopify.designMode) return;

      var items = this.querySelectorAll(".ss-acc__item");
      if (!items.length) return;

      var reduce = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      var timers = [];

      var schedule = function (item, i) {
        var delay = reduce ? 0 : AUTO_OPEN_DELAY + i * AUTO_OPEN_STAGGER;
        timers.push(
          window.setTimeout(function () {
            if (!modal.classList.contains("is-open")) return; // closed mid-stagger
            if (!item.open) expand(item); // only ever OPENS — never closes a row
          }, delay),
        );
      };

      for (var i = 0; i < count && i < items.length; i++) schedule(items[i], i);
      this._autoTimers = timers;
    }

    clearAutoOpen() {
      if (!this._autoTimers) return;
      for (var i = 0; i < this._autoTimers.length; i++)
        clearTimeout(this._autoTimers[i]);
      this._autoTimers = null;
    }

    onClick(e) {
      var summary = e.target.closest(".ss-acc__summary");
      if (!summary || !this.contains(summary)) return;
      e.preventDefault(); // take over the native instant toggle
      var item = summary.parentElement;
      if (item.open) collapse(item);
      else expand(item);
    }
  }

  customElements.define("ss-accordion", SSAccordion);

  // --- Theme editor: reveal what the merchant selects ---
  function openModalFor(host) {
    if (document.querySelector("ss-modal.is-open")) return;
    var id = host.getAttribute("data-ss-modal-adopt");
    var trigger = document.querySelector('[data-ss-modal-open="' + id + '"]');
    if (trigger) trigger.click();
  }

  document.addEventListener("shopify:block:select", function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var row = target.closest(".ss-acc__item");
    if (row) {
      var host = row.closest("[data-ss-modal-adopt]");
      if (host) openModalFor(host);
      // Expand after the adopt-move has reparented the row.
      requestAnimationFrame(function () {
        if (!row.classList.contains("is-open")) expand(row);
      });
      return;
    }

    // Wrapper block selected → just open its modal.
    var trig = target.querySelector
      ? target.querySelector("[data-ss-modal-open]")
      : null;
    if (trig && !document.querySelector("ss-modal.is-open")) trig.click();
  });
})();
